import {
    useContext,
    type FC
} from "react";
import {
    View
} from "react-native";
import type ITraceLineProps from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    usePointerType
} from "../../../../helpers/pointer";
import {
    CircleDashed as CircleDashedIcon,
    CircleCheck as CircleCheckIcon,
    CircleX as CircleXIcon,
    Ban as BanIcon
} from "lucide-react-native";
import {
    TraceInteractiveContext,
    TraceContext
} from "../../context";
import {
    getTraceLineColors
} from "../../stylesheet";
import {
    getTraceRowHeight
} from "../../util";
import TraceShimmerText from "../../internal/shimmerText";
import Loading from "../../../loading";
import Text from "../../../text";

const TraceLine: FC<ITraceLineProps> = ({
    customTheme: customThemeProp,
    icon: IconComponentProp,
    isConnected = false,
    isShimmer = false,
    status = "done",
    description,
    style,
    title,
    ...props
}) => {
    const traceContext = useContext(TraceContext);

    const interactive = useContext(TraceInteractiveContext);

    const customTheme = customThemeProp ?? traceContext?.customTheme;

    const {
        controlHeights,
        touchTargets,
        iconSizes,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const pointerType = usePointerType();

    const currentColors = getTraceLineColors({
        isActive: interactive.isActive,
        colors,
        status
    });

    const rowHeight = getTraceRowHeight({
        isInteractive: interactive.isInteractive,
        controlHeights,
        touchTargets,
        pointerType
    });

    const {
        description: descriptionDynamicStyle,
        connector: connectorDynamicStyle,
        container: containerDynamicStyle,
        icon: iconDynamicStyle
    } = useStyles({
        isInteractive: interactive.isInteractive,
        isConnected,
        iconSizes,
        rowHeight,
        borders,
        colors,
        spaces
    });

    const renderStatusIcon = () => {
        const iconProps = {
            color: currentColors.iconColor,
            size: iconSizes.small
        };

        if(status === "running") {
            return <Loading
                customTheme={customTheme}
                size={iconSizes.small}
                aria-hidden={true}
                color="mid"
            />;
        }

        if(status === "error") {
            return <CircleXIcon
                {...iconProps}
            />;
        }

        if(status === "pending") {
            return <CircleDashedIcon
                {...iconProps}
            />;
        }

        if(status === "cancelled") {
            return <BanIcon
                {...iconProps}
            />;
        }

        if(IconComponentProp) {
            return <IconComponentProp
                color={interactive.isActive ? "high" : "mid"}
                customColor={currentColors.iconColor}
                size={iconSizes.small}
            />;
        }

        return <CircleCheckIcon
            {...iconProps}
            color={colors.content.icon.success}
        />;
    };

    const renderTitle = () => {
        if(isShimmer) {
            return <TraceShimmerText
                customTheme={customTheme}
            >
                {title}
            </TraceShimmerText>;
        }

        return <Text
            customColor={currentColors.titleColor}
            customTheme={customTheme}
            style={stylesheet.title}
            variant="bodyMediumSize"
            numberOfLines={1}
        >
            {title}
        </Text>;
    };

    const renderDescription = () => {
        if(!description) {
            return null;
        }

        return <Text
            customColor={currentColors.descriptionColor}
            style={[
                stylesheet.description,
                descriptionDynamicStyle
            ]}
            customTheme={customTheme}
            variant="bodyMediumSize"
            numberOfLines={1}
        >
            {description}
        </Text>;
    };

    return <View
        {...props}
        style={[
            style,
            stylesheet.container,
            containerDynamicStyle
        ]}
    >
        <View
            style={[
                stylesheet.icon,
                iconDynamicStyle
            ]}
            aria-hidden={true}
        >
            {renderStatusIcon()}
        </View>
        {renderTitle()}
        {renderDescription()}
        <View
            style={[
                stylesheet.connector,
                connectorDynamicStyle
            ]}
            aria-hidden={true}
        />
    </View>;
};
export default TraceLine;
